import React, { useRef } from 'react';
import { Editor } from '@tiptap/react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { ColorPicker } from 'primereact/colorpicker';
import { classNames } from 'primereact/utils';
import { PrimeIcons } from 'primereact/api';
import { UrlPromptPopover, UrlPromptPopoverHandle } from './UrlPromptPopover';

const PARAGRAPH_FORMAT_OPTIONS = [
  { label: 'Normal', value: 'paragraph' },
  { label: 'Título 1', value: 'h1' },
  { label: 'Título 2', value: 'h2' },
  { label: 'Título 3', value: 'h3' },
  { label: 'Título 4', value: 'h4' },
];

const FONT_SIZE_OPTIONS = ['10', '12', '14', '16', '18', '20', '22', '24', '26', '28', '30', '36', '40'].map((size) => ({
  label: `${size}px`,
  value: `${size}px`,
}));

const LINE_HEIGHT_OPTIONS = [
  { label: 'Interlineado 1', value: '1' },
  { label: 'Interlineado 1.15', value: '1.15' },
  { label: 'Interlineado 1.5', value: '1.5' },
  { label: 'Interlineado 2', value: '2' },
];

// Buttons sit inside a narrow, horizontally-constrained toolbar, so tooltips
// are placed above them to avoid the default 'right' position colliding with
// the toolbar/viewport edge (which squeezes the tooltip into a tall, narrow column).
const TOOLTIP_OPTIONS = { position: 'top' as const };

interface Props {
  editor: Editor;
  hideFontSize?: boolean;
  disabled?: boolean;
  isFullscreen: boolean;
  showFullscreenButton: boolean;
  onToggleFullscreen: () => void;
  onRequestImageUpload: () => void;
  onInsertImageUrl: (url: string) => void;
}

export const RichTextEditorToolbar = ({
  editor,
  hideFontSize = false,
  disabled = false,
  isFullscreen,
  showFullscreenButton,
  onToggleFullscreen,
  onRequestImageUpload,
  onInsertImageUrl,
}: Props) => {
  const linkPopoverRef = useRef<UrlPromptPopoverHandle>(null);
  const imageUrlPopoverRef = useRef<UrlPromptPopoverHandle>(null);
  const videoPopoverRef = useRef<UrlPromptPopoverHandle>(null);

  const getActiveParagraphFormat = () => {
    if (editor.isActive('heading', { level: 1 })) return 'h1';
    if (editor.isActive('heading', { level: 2 })) return 'h2';
    if (editor.isActive('heading', { level: 3 })) return 'h3';
    if (editor.isActive('heading', { level: 4 })) return 'h4';
    return 'paragraph';
  };

  const handleParagraphFormatChange = (value: string) => {
    if (value === 'paragraph') {
      editor.chain().focus().setParagraph().run();
      return;
    }
    const level = Number(value.replace('h', '')) as 1 | 2 | 3 | 4;
    editor.chain().focus().toggleHeading({ level }).run();
  };

  const getActiveFontSize = () => editor.getAttributes('textStyle').fontSize || null;

  const getActiveLineHeight = () => {
    // Tiptap's LineHeight extension falls back to `element.style.lineHeight`
    // when parsing HTML, which returns '' (not null/undefined) when unset.
    const lineHeight = editor.getAttributes(editor.isActive('heading') ? 'heading' : 'paragraph').lineHeight;
    return lineHeight || '1';
  };

  const getActiveColor = () => (editor.getAttributes('textStyle').color || '#000000').replace('#', '');

  const handleLinkClick = (e: React.SyntheticEvent) => {
    const currentUrl = editor.getAttributes('link').href ?? '';
    linkPopoverRef.current?.show(e, currentUrl);
  };

  const handleVideoClick = (e: React.SyntheticEvent) => {
    videoPopoverRef.current?.show(e);
  };

  const handleImageByUrlClick = (e: React.SyntheticEvent) => {
    imageUrlPopoverRef.current?.show(e);
  };

  const Divider = ({ pushRight = false }: { pushRight?: boolean }) => (
    <span className={classNames('rich-text-toolbar-divider', { 'rich-text-toolbar-divider-push-right': pushRight })} />
  );

  return (
    <div className={classNames('rich-text-toolbar', { 'rich-text-toolbar-disabled': disabled })}>
      <Dropdown
        id='rich-text-paragraph-format'
        value={getActiveParagraphFormat()}
        options={PARAGRAPH_FORMAT_OPTIONS}
        onChange={(e) => handleParagraphFormatChange(e.value)}
        disabled={disabled}
        className='rich-text-toolbar-dropdown'
      />
      {!hideFontSize && (
        <Dropdown
          id='rich-text-font-size'
          value={getActiveFontSize()}
          options={FONT_SIZE_OPTIONS}
          placeholder='Tamaño'
          showClear
          onChange={(e) => (e.value ? editor.chain().focus().setFontSize(e.value).run() : editor.chain().focus().unsetFontSize().run())}
          disabled={disabled}
          className='rich-text-toolbar-dropdown'
        />
      )}

      <Divider />

      <Button
        type='button'
        label='B'
        className={classNames('rich-text-toolbar-btn-bold', { 'rich-text-toolbar-btn-active': editor.isActive('bold') })}
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={disabled}
        text
        tooltip='Negrita'
        tooltipOptions={TOOLTIP_OPTIONS}
      />
      <Button
        type='button'
        label='I'
        className={classNames('rich-text-toolbar-btn-italic', { 'rich-text-toolbar-btn-active': editor.isActive('italic') })}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={disabled}
        text
        tooltip='Cursiva'
        tooltipOptions={TOOLTIP_OPTIONS}
      />
      <Button
        type='button'
        label='U'
        className={classNames('rich-text-toolbar-btn-underline', { 'rich-text-toolbar-btn-active': editor.isActive('underline') })}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        disabled={disabled}
        text
        tooltip='Subrayado'
        tooltipOptions={TOOLTIP_OPTIONS}
      />
      <Button
        type='button'
        label='S'
        className={classNames('rich-text-toolbar-btn-strike', { 'rich-text-toolbar-btn-active': editor.isActive('strike') })}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={disabled}
        text
        tooltip='Tachado'
        tooltipOptions={TOOLTIP_OPTIONS}
      />
      <span className='rich-text-toolbar-color'>
        <span className='rich-text-toolbar-color-letter' aria-hidden='true'>
          A
        </span>
        <ColorPicker
          format='hex'
          value={getActiveColor()}
          onChange={(e) => editor.chain().focus().setColor(`#${e.value}`).run()}
          disabled={disabled}
        />
      </span>
      <Button
        type='button'
        icon='pi pi-eraser'
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        disabled={disabled}
        text
        tooltip='Borrar formato'
        tooltipOptions={TOOLTIP_OPTIONS}
      />

      <Divider />

      <Button
        type='button'
        icon={PrimeIcons.ALIGN_LEFT}
        className={classNames({ 'rich-text-toolbar-btn-active': editor.isActive({ textAlign: 'left' }) })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        disabled={disabled}
        text
      />
      <Button
        type='button'
        icon={PrimeIcons.ALIGN_CENTER}
        className={classNames({ 'rich-text-toolbar-btn-active': editor.isActive({ textAlign: 'center' }) })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        disabled={disabled}
        text
      />
      <Button
        type='button'
        icon={PrimeIcons.ALIGN_RIGHT}
        className={classNames({ 'rich-text-toolbar-btn-active': editor.isActive({ textAlign: 'right' }) })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        disabled={disabled}
        text
      />
      <Button
        type='button'
        icon={PrimeIcons.ALIGN_JUSTIFY}
        className={classNames({ 'rich-text-toolbar-btn-active': editor.isActive({ textAlign: 'justify' }) })}
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        disabled={disabled}
        text
      />

      <Divider />

      <Button
        type='button'
        icon={PrimeIcons.LIST}
        className={classNames({ 'rich-text-toolbar-btn-active': editor.isActive('bulletList') })}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        disabled={disabled}
        text
      />
      <Button
        type='button'
        icon='pi pi-list-check'
        className={classNames({ 'rich-text-toolbar-btn-active': editor.isActive('orderedList') })}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={disabled}
        text
      />
      <Dropdown
        id='rich-text-line-height'
        value={getActiveLineHeight()}
        options={LINE_HEIGHT_OPTIONS}
        // LineHeight is configured on the blocks, so setLineHeight (a textStyle mark) would drop the value (#125).
        onChange={(e) =>
          editor
            .chain()
            .focus()
            .updateAttributes('paragraph', { lineHeight: e.value })
            .updateAttributes('heading', { lineHeight: e.value })
            .run()
        }
        disabled={disabled}
        className='rich-text-toolbar-dropdown'
      />
      <Button
        type='button'
        icon='pi pi-angle-double-left'
        onClick={() => editor.chain().focus().outdent().run()}
        disabled={disabled}
        text
        tooltip='Reducir sangría'
        tooltipOptions={TOOLTIP_OPTIONS}
      />
      <Button
        type='button'
        icon='pi pi-angle-double-right'
        onClick={() => editor.chain().focus().indent().run()}
        disabled={disabled}
        text
        tooltip='Aumentar sangría'
        tooltipOptions={TOOLTIP_OPTIONS}
      />
      <Button
        type='button'
        icon={PrimeIcons.COMMENT}
        className={classNames({ 'rich-text-toolbar-btn-active': editor.isActive('blockquote') })}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        disabled={disabled}
        text
        tooltip='Cita'
        tooltipOptions={TOOLTIP_OPTIONS}
      />

      <Divider />

      <Button
        type='button'
        icon={PrimeIcons.LINK}
        className={classNames({ 'rich-text-toolbar-btn-active': editor.isActive('link') })}
        onClick={handleLinkClick}
        disabled={disabled}
        text
        tooltip='Insertar enlace'
        tooltipOptions={TOOLTIP_OPTIONS}
      />
      <UrlPromptPopover
        ref={linkPopoverRef}
        id='rich-text-link-url'
        label='URL del enlace'
        placeholder='https://ejemplo.com'
        onSubmit={(url) => editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()}
      />
      <Button
        type='button'
        icon={PrimeIcons.IMAGE}
        onClick={onRequestImageUpload}
        disabled={disabled}
        text
        tooltip='Subir imagen'
        tooltipOptions={TOOLTIP_OPTIONS}
      />
      <Button
        type='button'
        icon='pi pi-images'
        onClick={handleImageByUrlClick}
        disabled={disabled}
        text
        tooltip='Imagen desde URL'
        tooltipOptions={TOOLTIP_OPTIONS}
      />
      <UrlPromptPopover
        ref={imageUrlPopoverRef}
        id='rich-text-image-url'
        label='URL de la imagen'
        placeholder='https://ejemplo.com/imagen.jpg'
        onSubmit={onInsertImageUrl}
      />
      <Button
        type='button'
        icon={PrimeIcons.VIDEO}
        onClick={handleVideoClick}
        disabled={disabled}
        text
        tooltip='Insertar vídeo'
        tooltipOptions={TOOLTIP_OPTIONS}
      />
      <UrlPromptPopover
        ref={videoPopoverRef}
        id='rich-text-video-url'
        label='URL del vídeo (YouTube, Vimeo, Google Drive u otro)'
        placeholder='https://youtube.com/watch?v=...'
        onSubmit={(url) => editor.commands.setVideoEmbed(url)}
      />
      <Button
        type='button'
        icon={PrimeIcons.TABLE}
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        disabled={disabled}
        text
        tooltip='Insertar tabla'
        tooltipOptions={TOOLTIP_OPTIONS}
      />
      <Button
        type='button'
        icon='pi pi-minus'
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        disabled={disabled}
        text
        tooltip='Línea horizontal'
        tooltipOptions={TOOLTIP_OPTIONS}
      />

      <Divider pushRight />

      <Button
        type='button'
        icon={PrimeIcons.UNDO}
        onClick={() => editor.chain().focus().undo().run()}
        disabled={disabled || !editor.can().undo()}
        text
      />
      <Button
        type='button'
        icon={PrimeIcons.UNDO}
        className='rich-text-toolbar-btn-redo'
        onClick={() => editor.chain().focus().redo().run()}
        disabled={disabled || !editor.can().redo()}
        text
      />
      {showFullscreenButton && (
        <Button
          type='button'
          icon={isFullscreen ? PrimeIcons.WINDOW_MINIMIZE : PrimeIcons.WINDOW_MAXIMIZE}
          className={classNames({ 'rich-text-toolbar-btn-active': isFullscreen })}
          onClick={onToggleFullscreen}
          disabled={disabled}
          text
          tooltip={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
          tooltipOptions={TOOLTIP_OPTIONS}
        />
      )}
    </div>
  );
};
